import { Static, Type } from '@sinclair/typebox';
import { ReservationAmountDiscountSource } from './translation_namespace/reservation_discount_namespace';

/**
 * Montants du récap du tunnel de réservation (spec Promotions § 8), même bloc dans `cart-summary` et
 * dans la pré-réservation : total au prix normal, remises sur leur ligne, total après remise, bons,
 * puis, une fois la pré-réservation créée, ce qui se paie en ligne et sur place.
 *
 * Chaînes formatées serveur (devise et locale du pro), remises et bons déjà signés « - » ; valeurs
 * brutes en centimes, toujours positives. Le front ne compose aucun signe et ne compare aucune chaîne.
 * Module neutre : repris par `booking-tunnel.ts` et `pre_reservation.ts` sans cycle d'import.
 */
export const BookingAmountsDiscountLineSchema = Type.Object({
  /** Libellé de l'offre (libellé du code promo, à défaut le code) ; vide pour une remise accordée par le pro. */
  label: Type.String(),
  code: Type.Optional(Type.String()),
  /** `MANUAL` : remise posée par le pro depuis l'agenda (libellé « Remise accordée ») ; absent = code promo. */
  kind: Type.Optional(Type.Enum(ReservationAmountDiscountSource)),
  amount: Type.String(),
  amount_raw: Type.Number(),
});
export type BookingAmountsDiscountLineObject = Static<typeof BookingAmountsDiscountLineSchema>;

export const BookingAmountsVoucherLineSchema = Type.Object({
  code: Type.String(),
  /** `VoucherType` : AMOUNT (carte cadeau), PACK ou PRESTATION. */
  voucher_type: Type.String(),
  amount: Type.String(),
  amount_raw: Type.Number(),
});
export type BookingAmountsVoucherLineObject = Static<typeof BookingAmountsVoucherLineSchema>;

export const BookingAmountsSchema = Type.Object({
  /** Total au prix normal, réglé sur place compris. */
  gross_total: Type.String(),
  gross_total_raw: Type.Number(),
  /** Absent sans remise. */
  discount_lines: Type.Optional(Type.Array(BookingAmountsDiscountLineSchema)),
  /** Total après remise, avant bons : affiché avec le total au prix normal barré quand ils diffèrent. */
  net_total: Type.String(),
  net_total_raw: Type.Number(),
  /** Total des remises (prix normal − total après remise), absent sans remise : « Vous avez économisé … ». */
  savings: Type.Optional(Type.String()),
  savings_raw: Type.Optional(Type.Number()),
  /** Absent sans bon. */
  voucher_lines: Type.Optional(Type.Array(BookingAmountsVoucherLineSchema)),
  /** Total après remise et bons : ce qui reste à régler, en ligne et sur place. */
  due_total: Type.String(),
  due_total_raw: Type.Number(),
  /** Pré-réservation seulement (conditions de paiement connues). */
  amount_to_pay_today: Type.Optional(Type.String()),
  amount_to_pay_today_raw: Type.Optional(Type.Number()),
  remaining_on_site: Type.Optional(Type.String()),
  remaining_on_site_raw: Type.Optional(Type.Number()),
});
export type BookingAmountsObject = Static<typeof BookingAmountsSchema>;
